import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatCurrency } from '../lib/utils';
import axios from 'axios';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { BarChart3, TrendingUp, TrendingDown, DollarSign, Printer, Download, Loader2, Calendar } from 'lucide-react';
import WeeklyReport from '../components/WeeklyReport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#c2185b', '#4caf50', '#ff9800', '#2196f3', '#9c27b0', '#00bcd4'];

export default function ReportsPage() {
  const { t, language } = useLanguage();
  const [view, setView] = useState('standard'); // 'standard' | 'weekly'
  const [period, setPeriod] = useState('month');
  const [customDateRange, setCustomDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [salesReport, setSalesReport] = useState(null);
  const [expensesReport, setExpensesReport] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (period !== 'custom') {
      fetchReports();
    }
  }, [period]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      let url = `${API}/reports/sales?period=${period}`;
      let expUrl = `${API}/reports/expenses?period=${period}`;
      
      if (period === 'custom') {
        url = `${API}/reports/sales?period=custom&start_date=${customDateRange.start_date}&end_date=${customDateRange.end_date}`;
        expUrl = `${API}/reports/expenses?period=custom&start_date=${customDateRange.start_date}&end_date=${customDateRange.end_date}`;
      }
      
      const [salesRes, expensesRes, statsRes] = await Promise.all([
        axios.get(url),
        axios.get(expUrl),
        axios.get(`${API}/dashboard/stats`),
      ]);
      setSalesReport(salesRes.data);
      setExpensesReport(expensesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomDateSearch = () => {
    if (period === 'custom') {
      fetchReports();
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const [exporting, setExporting] = React.useState(false);
  
  const handleExportReport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export/financial-report?period=${period}`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = downloadUrl;
      a.download = `financial_report_${period}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      console.error('Export error:', error);
      alert('فشل التصدير - Export failed');
    } finally {
      setExporting(false);
    }
  };

  const periodLabels = {
    day: language === 'ar' ? 'يومي' : 'Daily',
    week: language === 'ar' ? 'أسبوعي' : 'Weekly',
    month: language === 'ar' ? 'شهري' : 'Monthly',
    year: language === 'ar' ? 'سنوي' : 'Yearly',
    custom: language === 'ar' ? 'تاريخ محدد' : 'Custom Date',
  };

  const profitData = salesReport && expensesReport ? [
    { name: language === 'ar' ? 'المبيعات' : 'Sales', value: salesReport.total, color: '#4caf50' },
    { name: language === 'ar' ? 'المصروفات' : 'Expenses', value: expensesReport.total, color: '#f44336' },
    { name: language === 'ar' ? 'المشتريات' : 'Purchases', value: stats?.month_purchases || 0, color: '#ff9800' },
  ] : [];

  const netProfit = (salesReport?.total || 0) - (expensesReport?.total || 0) - (stats?.month_purchases || 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl" data-testid="reports-title">{t('reports')}</h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'التقارير المالية والإحصائيات' : 'Financial reports and statistics'}
          </p>
        </div>
        <div className="flex gap-2 no-print">
          <Button 
            variant="outline" 
            onClick={handleExportReport} 
            disabled={exporting}
            data-testid="export-report-btn"
          >
            {exporting ? (
              <Loader2 size={18} className="me-2 animate-spin" />
            ) : (
              <Download size={18} className="me-2" />
            )}
            {exporting 
              ? (language === 'ar' ? 'جاري التصدير...' : 'Exporting...') 
              : (language === 'ar' ? 'تصدير Excel' : 'Export Excel')}
          </Button>
          <Button variant="outline" onClick={handlePrint} data-testid="print-report-btn">
            <Printer size={18} className="me-2" />
            {t('print')}
          </Button>
        </div>
      </div>

      {/* View switch */}
      <Tabs value={view} onValueChange={setView} className="no-print">
        <TabsList className="grid grid-cols-2 w-full max-w-sm">
          <TabsTrigger value="standard" data-testid="view-standard">
            {language === 'ar' ? 'تقارير عامة' : 'Standard'}
          </TabsTrigger>
          <TabsTrigger value="weekly" data-testid="view-weekly">
            {language === 'ar' ? 'تقرير أسبوعي' : 'Weekly Report'}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {view === 'weekly' ? (
        <WeeklyReport />
      ) : (
        <>
      {/* Period Tabs */}
      <Tabs value={period} onValueChange={setPeriod} className="no-print">
        <TabsList className="grid grid-cols-5 w-full max-w-lg">
          <TabsTrigger value="day" data-testid="period-day">{periodLabels.day}</TabsTrigger>
          <TabsTrigger value="week" data-testid="period-week">{periodLabels.week}</TabsTrigger>
          <TabsTrigger value="month" data-testid="period-month">{periodLabels.month}</TabsTrigger>
          <TabsTrigger value="year" data-testid="period-year">{periodLabels.year}</TabsTrigger>
          <TabsTrigger value="custom" data-testid="period-custom">
            <Calendar className="h-4 w-4 me-1" />
            {periodLabels.custom}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Custom Date Range */}
      {period === 'custom' && (
        <Card className="border-primary/30 bg-primary/5 no-print">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'من تاريخ' : 'From Date'}</Label>
                <Input
                  type="date"
                  value={customDateRange.start_date}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, start_date: e.target.value })}
                  className="w-40"
                  data-testid="custom-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'إلى تاريخ' : 'To Date'}</Label>
                <Input
                  type="date"
                  value={customDateRange.end_date}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end_date: e.target.value })}
                  className="w-40"
                  data-testid="custom-end-date"
                />
              </div>
              <Button onClick={handleCustomDateSearch} data-testid="custom-search-btn">
                <BarChart3 className="h-4 w-4 me-2" />
                {language === 'ar' ? 'عرض التقرير' : 'Show Report'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-border/40">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('sales')}</p>
                    <p className="text-2xl font-bold text-green-600" data-testid="report-total-sales">
                      {formatCurrency(salesReport?.total || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {salesReport?.count || 0} {language === 'ar' ? 'عملية' : 'transactions'}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="text-green-600" size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/40">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('expenses')}</p>
                    <p className="text-2xl font-bold text-red-600" data-testid="report-total-expenses">
                      {formatCurrency(expensesReport?.total || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {expensesReport?.count || 0} {language === 'ar' ? 'عملية' : 'transactions'}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <TrendingDown className="text-red-600" size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/40">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('purchases')}</p>
                    <p className="text-2xl font-bold text-orange-600" data-testid="report-total-purchases">
                      {formatCurrency(stats?.month_purchases || 0)}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <BarChart3 className="text-orange-600" size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`border-border/40 ${netProfit >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('netProfit')}</p>
                    <p
                      className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}
                      data-testid="report-net-profit"
                    >
                      {formatCurrency(netProfit)}
                    </p>
                  </div>
                  <div className={`w-12 h-12 ${netProfit >= 0 ? 'bg-green-100' : 'bg-red-100'} rounded-xl flex items-center justify-center`}>
                    <DollarSign className={netProfit >= 0 ? 'text-green-600' : 'text-red-600'} size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sales Trend Chart */}
            <Card className="border-border/40">
              <CardHeader>
                <CardTitle className="font-heading text-xl">
                  {language === 'ar' ? 'اتجاه المبيعات' : 'Sales Trend'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]" data-testid="sales-trend-chart">
                  {salesReport?.chart_data?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={salesReport.chart_data}>
                        <defs>
                          <linearGradient id="colorSalesReport" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4caf50" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#4caf50" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(value) => [formatCurrency(value), t('total')]} />
                        <Area
                          type="monotone"
                          dataKey="total"
                          stroke="#4caf50"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorSalesReport)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground">
                      {t('noData')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Profit Breakdown */}
            <Card className="border-border/40">
              <CardHeader>
                <CardTitle className="font-heading text-xl">
                  {language === 'ar' ? 'توزيع الأرباح' : 'Profit Breakdown'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]" data-testid="profit-breakdown-chart">
                  {profitData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={profitData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                          {profitData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground">
                      {t('noData')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Expenses by Category */}
            <Card className="border-border/40">
              <CardHeader>
                <CardTitle className="font-heading text-xl">
                  {language === 'ar' ? 'المصروفات حسب الفئة' : 'Expenses by Category'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]" data-testid="expenses-category-chart">
                  {expensesReport?.by_category?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={expensesReport.by_category}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="amount"
                          nameKey="category"
                          label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        >
                          {expensesReport.by_category.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground">
                      {t('noData')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="border-border/40">
              <CardHeader>
                <CardTitle className="font-heading text-xl">{t('quickStats')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <span className="text-muted-foreground">
                      {language === 'ar' ? 'متوسط قيمة البيع' : 'Average Sale Value'}
                    </span>
                    <span className="font-bold text-primary">
                      {formatCurrency(salesReport?.average || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <span className="text-muted-foreground">
                      {language === 'ar' ? 'عدد المنتجات' : 'Total Products'}
                    </span>
                    <span className="font-bold">{stats?.products_count || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <span className="text-muted-foreground">
                      {language === 'ar' ? 'عدد العملاء' : 'Total Customers'}
                    </span>
                    <span className="font-bold">{stats?.customers_count || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <span className="text-muted-foreground">
                      {language === 'ar' ? 'إجمالي الديون' : 'Total Debts'}
                    </span>
                    <span className="font-bold text-destructive">
                      {formatCurrency(stats?.total_debt || 0)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
}
